import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function ExpensePharmacyFilter({ selectedPharmacies = [], onChange, currentUser }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelected, setTempSelected] = useState(selectedPharmacies);

  useEffect(() => {
    fetchPharmacies();
  }, []);

  useEffect(() => {
    setTempSelected(selectedPharmacies);
  }, [selectedPharmacies]);

  const fetchPharmacies = async () => {
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const response = await axios.get(`${API}/pharmacies`, { headers });
      setPharmacies(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const filteredPharmacies = pharmacies.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (pharmacyId) => {
    setTempSelected(prev =>
      prev.includes(pharmacyId)
        ? prev.filter(id => id !== pharmacyId)
        : [...prev, pharmacyId]
    );
  };

  const handleConfirm = () => {
    onChange(tempSelected);
    setIsOpen(false);
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  const handleRemoveBadge = (pharmacyId) => {
    const newSelected = selectedPharmacies.filter(id => id !== pharmacyId);
    onChange(newSelected);
  };

  const selectedCount = selectedPharmacies.length;

  if (currentUser?.role !== 'super_admin') return null;

  return (
    <div className="relative">
      {/* Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-2xl bg-white hover:border-gray-300 transition-colors cursor-pointer"
      >
        <Building2 className="w-6 h-6 text-emerald-600" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-700 font-semibold">Pharmacies</span>
            <span className="text-sm text-gray-500">{selectedCount}</span>
          </div>
        </div>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>

      {/* Badges des pharmacies sélectionnées */}
      {selectedPharmacies.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {selectedPharmacies.map(pharmId => {
            const pharmacy = pharmacies.find(p => p.id === pharmId);
            if (!pharmacy) return null;
            return (
              <div
                key={pharmId}
                className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full border border-emerald-200"
              >
                <span className="text-emerald-600 font-bold">✚</span>
                <span className="font-medium">{pharmacy.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveBadge(pharmId);
                  }}
                  className="text-emerald-500 hover:text-emerald-700 ml-1 text-lg"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-[400px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-gray-900">Pharmacies</span>
                <span className="text-sm text-gray-500">{tempSelected.length}</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>

            {/* Barre de recherche */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher une pharmacie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-200"
              />
            </div>

            {/* Liste avec checkboxes */}
            <div className="max-h-[300px] overflow-y-auto mb-4 space-y-2">
              {filteredPharmacies.map(pharmacy => (
                <label
                  key={pharmacy.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={tempSelected.includes(pharmacy.id)}
                    onCheckedChange={() => handleToggle(pharmacy.id)}
                    className="border-emerald-500 data-[state=checked]:bg-emerald-500"
                  />
                  <Building2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-gray-700 font-medium">{pharmacy.name}</span>
                  {tempSelected.includes(pharmacy.id) && (
                    <svg className="w-4 h-4 text-emerald-600 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  )}
                </label>
              ))}
            </div>

            {/* Boutons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <Button
                variant="ghost"
                onClick={handleClearAll}
                className="text-gray-600 hover:text-gray-900"
              >
                Effacer tout
              </Button>
              <Button
                onClick={handleConfirm}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ExpensePharmacyFilter;
